/**
 * TODO - preamble
 */
import React, { useEffect } from 'react';
import { FormattedHTMLMessage, injectIntl } from 'react-intl';
import { produce } from 'immer';

import {
    handleEmailOnKeyDown,
    submitForm,
    updateFieldValidationErrorsByFieldId,
    updateViewModelOrganization,
    shouldCollectAddressFields,
    HowDoesVerifyingWorkComponent as HowDoesVerifyingWork,
    LogoComponent,
    PhoneNumberComponent as PhoneNumber,
    FirstNameComponent as FirstName,
    LastNameComponent as LastName,
    EmailComponent as Email,
    MarketConsentWrapper,
    AddressComponent,
    CityComponent,
    StateComponent,
    CompanyComponent,
    PostalCodeComponent,
    FormFooterComponent as FormFooter,
    RewardsRemainingComponent,
    VerificationStepsEnum,
    isFormFilled,
    setRef,
    VerificationService,
    Organization,
    EmploymentPersonalInfoViewModel,
} from '@sheerid/jslib';

import { ACCEPTABLE_ORGS } from '../constants';
import { getQueryParamsByName } from '../lib/utils/browser';

  interface StepEmploymentPersonalInfoComponentProps {
      verificationService: VerificationService;
  }

const StepEmploymentPersonalInfo = ({ verificationService }: StepEmploymentPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as EmploymentPersonalInfoViewModel;
    const { fieldValidationErrors } = verificationService;
    const updateEmploymentViewModel = (key: keyof EmploymentPersonalInfoViewModel, value: any) => {
        const nextState: EmploymentPersonalInfoViewModel = produce(viewModel, (draft: EmploymentPersonalInfoViewModel) => {
            (draft[key] as any) = value;
        });
        verificationService.updateViewModel(nextState);
    };

    function getVerificationPeriod() {
        const month = new Date().getMonth() + 1;
        const verificationPeriod = (month >= 3 && month <= 8 ? 'spring' : 'fall') as string;
        return verificationPeriod;
    }

    useEffect(() => {
        const qspOrgIdNumber = Number.parseInt(getQueryParamsByName('orgId'), 10) || 0;
        const company = ACCEPTABLE_ORGS.find((org) => org.id === qspOrgIdNumber);

        const nextState: EmploymentPersonalInfoViewModel = produce(
            viewModel,
            (draft: EmploymentPersonalInfoViewModel) => {
                draft.metadata = {
                    ...viewModel.metadata,
                    verificationPeriod: getVerificationPeriod(),
                };
                if (company) {
                    draft.organization = company;
                    if (!draft.fieldsToSkipValidation) draft.fieldsToSkipValidation = [];

                    if (!draft.fieldsToSkipValidation.includes('organization')) {
                        draft.fieldsToSkipValidation.push('organization');
                    }
                }
            },
        );

        verificationService.updateViewModel(nextState);
    }, []);

    return (
        <div id="sid-step-employment-personal-info" className="sid-l-container">
            <div className="sid-header">
                <div className="sid-l-horz-center">
                    <LogoComponent verificationService={verificationService} />
                </div>
                <div className="sid-header__title sid-l-horz-center">
                    <FormattedHTMLMessage id="step.personalInfo.title" defaultMessage="Unlock this Employment-Only Offer" tagName="h1" />
                </div>

                <div className="sid-header__subtitle sid-l-horz-center">
                    <FormattedHTMLMessage id="step.personalInfo.subtitle" defaultMessage="Verify you're a current employee at an eligible company." />
                      &nbsp;
                    <HowDoesVerifyingWork verificationService={verificationService} />
                    <RewardsRemainingComponent verificationService={verificationService} />
                </div>
            </div>

            <CompanyComponent
                value={(verificationService.viewModel as EmploymentPersonalInfoViewModel).organization}
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.organization}
                onChange={(choice: Organization) => {
                    updateViewModelOrganization(choice, verificationService);
                    updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                }}
            />

            <div className="sid-names">
                <FirstName
                    value={viewModel.firstName}
                    isErrored={!!fieldValidationErrors.firstName}
                    onChange={(newValue: string) => {
                        updateEmploymentViewModel('firstName', newValue);
                        updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                    }}
                />

                <LastName
                    value={viewModel.lastName}
                    isErrored={!!fieldValidationErrors.lastName}
                    onChange={(newValue: string) => {
                        updateEmploymentViewModel('lastName', newValue);
                        updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                    }}
                />
            </div>

            {shouldCollectAddressFields(viewModel.countryChoice, verificationService.programTheme.config.countries) ? (
                <AddressComponent
                    isErrored={!!fieldValidationErrors.address1}
                    errorId={fieldValidationErrors.address1}
                    onChange={(newValue: string) => {
                        updateEmploymentViewModel('address1', newValue);
                        updateFieldValidationErrorsByFieldId('address1', newValue, verificationService);
                    }}
                    value={viewModel.address1}
                />
            ) : null}

            {shouldCollectAddressFields(viewModel.countryChoice, verificationService.programTheme.config.countries) ? (
                <div className="sid-thirds">
                    <CityComponent
                        isErrored={!!fieldValidationErrors.city}
                        errorId={fieldValidationErrors.city}
                        onChange={(newValue: string) => {
                            updateEmploymentViewModel('city', newValue);
                            updateFieldValidationErrorsByFieldId('city', newValue, verificationService);
                        }}
                        value={viewModel.city}
                    />

                    <StateComponent
                        isErrored={!!fieldValidationErrors.state}
                        errorId={fieldValidationErrors.state}
                        onChange={(newValue: string) => {
                            updateEmploymentViewModel('state', newValue);
                            updateFieldValidationErrorsByFieldId('state', newValue, verificationService);
                        }}
                        value={viewModel.state}
                    />

                    <PostalCodeComponent
                        isErrored={!!fieldValidationErrors.postalCode}
                        onChange={(newValue: string) => {
                            updateEmploymentViewModel('postalCode', newValue);
                            updateFieldValidationErrorsByFieldId('postalCode', newValue, verificationService);
                        }}
                        value={viewModel.postalCode}
                    />
                </div>
            ) : null}

            <Email
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                explanation={<FormattedHTMLMessage id="employment.emailExplanation" defaultMessage="Use your work email if you have one" />}
                onChange={(newValue: string) => {
                    updateEmploymentViewModel('email', newValue);
                    updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                }}
                onKeyDown={(event: any) => handleEmailOnKeyDown(event)}
            />
            {verificationService.programTheme.isSmsNotifierConfigured || verificationService.programTheme.smsLoopEnabled ? (
                <PhoneNumber
                    isRequired={!!verificationService.programTheme.smsLoopEnabled}
                    value={viewModel.phoneNumber}
                    isErrored={!!fieldValidationErrors.phoneNumber}
                    onChange={(newValue: string) => {
                        updateEmploymentViewModel('phoneNumber', newValue);
                        updateFieldValidationErrorsByFieldId('phoneNumber', newValue, verificationService);
                    }}
                    selectedCountryCode={viewModel.countryChoice && viewModel.countryChoice.value}
                />
            ) : null}
            <MarketConsentWrapper
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.marketConsentValue}
                onChange={(newValue: string) => {
                    updateEmploymentViewModel('metadata', {
                        ...verificationService.viewModel.metadata,
                        marketConsentValue: newValue,
                    });
                    updateFieldValidationErrorsByFieldId('marketConsentValue', newValue, verificationService);
                }}
                viewModel={viewModel}
            />

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        id="sid-submit-wrapper__collect-info"
                        onClick={() => submitForm(viewModel, verificationService, VerificationStepsEnum.collectEmployeePersonalInfo)}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${!isFormFilled(viewModel, verificationService.formValidationOptions) ? 'sid-btn--disabled-like' : ''}`}
                        aria-label="submit"
                        aria-labelledby="verify-status-text"
                        ref={(button) => setRef('submitButton', button)}
                    >
                        <span id="verify-status-text">
                            <FormattedHTMLMessage id="verifyMyEmploymentStatus" defaultMessage="Verify My Employment Status" />
                        </span>
                    </button>
                </div>
            </div>

            <FormFooter verificationService={verificationService} />
        </div>
    );
};

// TODO remove injectIntl, unused
export const StepEmploymentPersonalInfoComponent = injectIntl(StepEmploymentPersonalInfo);
